import { useTranslation } from 'react-i18next'
import { Skeleton } from '@heroui/react'
import { useStoreProductDetails } from '@/hooks/use-store-product-details'
import { useAppSelector } from '@/redux/hooks'
import { ProductDetails } from './product-details'
import { DEFAULT_COUNTRY_CODE } from '../helpers/constants'

interface ProductDetailsDrawerProps {
    onClose: () => void
    categoryIndex: number
    productId?: string
    returnedItemId: string
}

export const ProductDetailsDrawer = ({
    onClose,
    categoryIndex,
    returnedItemId,
    productId
}: ProductDetailsDrawerProps) => {
    const {
        t,
        i18n: { language }
    } = useTranslation()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const currency = useAppSelector(
        (store) => store.returnsApi.order.response?.order?.money_format
    )?.currency

    const currentReturn = useAppSelector((store) => store.return)

    const { data, status } = useStoreProductDetails({
        ecommerceId: settings.ecommerce_id,
        language,
        currency,
        countryCode:
            currentReturn.returnAddress.country_code ?? DEFAULT_COUNTRY_CODE,
        productId: productId
    })

    if (status === 'pending') {
        return (
            <div className="flex h-[80vh] max-h-[615px] items-center justify-center">
                <div className="flex size-full gap-10 p-4 pb-10 pt-12 xl:px-12 2xl:max-w-7xl">
                    <div className="flex w-full max-w-[30.875rem] gap-2">
                        <div className="flex flex-col gap-2 overflow-auto pr-1.5">
                            {[...Array(6)].map((_, i) => (
                                <Skeleton
                                    className="min-h-16 w-16 rounded-md"
                                    key={i}
                                />
                            ))}
                        </div>
                        <Skeleton className="size-full flex-1 shrink-0 rounded-md" />
                    </div>
                    <div className="flex-1">
                        <Skeleton className="h-8 w-72 rounded-md" />
                        <div className="pb-6 pt-4">
                            <Skeleton className="min-h-16 w-full rounded-md" />
                        </div>
                        <div className="space-y-5">
                            <Skeleton className="h-5 w-40 rounded-md" />
                        </div>
                        <div className="flex flex-col gap-2 pt-4">
                            {[...Array(5)].map((_, index) => (
                                <Skeleton
                                    className="min-h-[60px] w-full rounded-md"
                                    key={index}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (status === 'error') {
        return (
            <div className="flex h-[80vh] max-h-[615px] items-center justify-center">
                {t('toast_errors.error_406')}
            </div>
        )
    }

    return (
        <ProductDetails
            product={data}
            categoryIndex={categoryIndex}
            returnedItemId={returnedItemId}
            onClose={onClose}
        />
    )
}
